import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { factory } from "../../api/apiFactory";
import { showNotification } from "./notificationSlice";

const transferRequestApi = factory.get("transferRequest");

const initialReview = {
  dialog: false,
  loading: false,
  request: null,
};

const initialState = {
  data: [],
  total: 0,
  loading: false,
  filters: {
    page: 0,
    skip: 0,
    take: 15,
    status: "pending",
    from_id: null,
    to_id: null,
    item_id: null,
    start_date: null,
    end_date: null,
  },
  filtersDialog: false,
  review: { ...initialReview },
};

export const transferRequestSlice = createSlice({
  name: "transferRequest",
  initialState,
  reducers: {
    setLoading: (state) => {
      state.loading = !state.loading;
    },
    setData: (state, action) => {
      // Some transfer-request list responses wrap the collection in an extra
      // array (e.g. [[{req}, ...]]); flatten one level so the grid receives a
      // plain array of request objects. Harmless when already flat.
      state.data = (action.payload.data.data || []).flat();
      state.total = action.payload.data.total;
    },
    setFilters: (state, { payload }) => {
      state.filters = { ...state.filters, ...payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    setFiltersDialog: (state) => {
      state.filtersDialog = !state.filtersDialog;
    },
    openReview: (state, { payload }) => {
      state.review = {
        ...initialReview,
        dialog: true,
        request: payload.request,
      };
    },
    closeReview: (state) => {
      state.review = { ...initialReview };
    },
    setReviewLoading: (state) => {
      state.review.loading = !state.review.loading;
    },
  },
});

export const {
  setLoading,
  setData,
  setFilters,
  resetFilters,
  setFiltersDialog,
  openReview,
  closeReview,
  setReviewLoading,
} = transferRequestSlice.actions;

export default transferRequestSlice.reducer;

const index = () => async (dispatch, getState) => {
  try {
    const filters = getState().transferRequest.filters;
    dispatch(setLoading());
    const res = await transferRequestApi.index({
      page: filters.page,
      skip: filters.skip,
      take: filters.take,
      status: filters.status,
      from_id: filters.from_id?.id,
      to_id: filters.to_id?.id,
      item_id: filters.item_id?.id,
      start_date: filters.start_date
        ? dayjs(filters.start_date).format("YYYY-MM-DD")
        : null,
      end_date: filters.end_date
        ? dayjs(filters.end_date).format("YYYY-MM-DD")
        : null,
    });
    dispatch(setData(res));
    dispatch(setLoading());
  } catch (err) {
    dispatch(setLoading());
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to fetch data",
      })
    );
  }
};

const accept = (id) => async (dispatch) => {
  try {
    dispatch(setReviewLoading());
    await transferRequestApi.accept(id);
    dispatch(setReviewLoading());
    dispatch(
      showNotification({
        type: "success",
        message: "Request accepted",
      })
    );
    dispatch(closeReview());
    dispatch(index());
  } catch (err) {
    dispatch(setReviewLoading());
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to accept request",
      })
    );
  }
};

const reject = (id, reason) => async (dispatch) => {
  try {
    dispatch(setReviewLoading());
    await transferRequestApi.reject(id, { reject_reason: reason });
    dispatch(setReviewLoading());
    dispatch(
      showNotification({
        type: "success",
        message: "Request rejected",
      })
    );
    dispatch(closeReview());
    dispatch(index());
  } catch (err) {
    dispatch(setReviewLoading());
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to reject request",
      })
    );
  }
};

export const TransferRequest = {
  index,
  accept,
  reject,
};
